import { useEffect, useMemo, useRef, useState } from "react";

import { Box, Button, Group, Menu, Stack } from "@mantine/core";
import { DatePicker, type DatesRangeValue } from "@mantine/dates";

import { IconCalendarMinus, IconChevronDown } from "@tabler/icons-react";
import { format } from "date-fns";

import type { EligibilityAppointmentCalendarDateRange } from "@/billing/types/eligibility-appointment-date-range";
import {
  clampEligibilityAppointmentCalendarDateRange,
  formatEligibilityAppointmentDateRangeLabel,
  getDefaultEligibilityAppointmentCalendarDateRange,
  getEligibilityAppointmentDateRangeMaxEndDate,
  isDefaultEligibilityAppointmentCalendarDateRange,
} from "@/billing/utils/eligibility-appointment-date-range";
import { TextWrapper } from "@/components/TextWrapper";
import { PRACTICE_CALENDAR_DATE_FORMAT } from "@/shared/practice-timezone";

import {
  ELIGIBILITY_DATE_RANGE_PRESET_CUSTOM,
  ELIGIBILITY_DATE_RANGE_PRESET_CUSTOM_LABEL,
  ELIGIBILITY_DATE_RANGE_PRESET_NEXT_TWO_WEEKS,
  ELIGIBILITY_DATE_RANGE_PRESET_NEXT_TWO_WEEKS_LABEL,
} from "./constants/eligibility";

interface EligibilityDateRangeControlProps {
  calendarDateRange: EligibilityAppointmentCalendarDateRange;
  onCalendarDateRangeChange: (calendarDateRange: EligibilityAppointmentCalendarDateRange) => void;
  /** Practice-local calendar date (`yyyy-MM-dd`). */
  today: string;
  /** Disables the trigger while `today` is still read in a fallback timezone. */
  isDisabled?: boolean;
}

type EligibilityDateRangePreset =
  | typeof ELIGIBILITY_DATE_RANGE_PRESET_NEXT_TWO_WEEKS
  | typeof ELIGIBILITY_DATE_RANGE_PRESET_CUSTOM;

type EligibilityDateRangeMenuView = "menu" | "calendar";

function toDatePickerRangeValue(
  calendarDateRange: EligibilityAppointmentCalendarDateRange,
): DatesRangeValue {
  return [calendarDateRange.startCalendarDate, calendarDateRange.endCalendarDate];
}

/**
 * Builds a calendar-date range from a Mantine range value, clamped to the allowed window.
 * Returns undefined when either date is missing.
 */
function resolveCalendarDateRangeFromPickerValue(
  value: DatesRangeValue,
  today: string,
): EligibilityAppointmentCalendarDateRange | undefined {
  const [rawStart, rawEnd] = value;
  if (!rawStart || !rawEnd) return undefined;
  return clampEligibilityAppointmentCalendarDateRange(
    {
      startCalendarDate: toPickerCalendarDate(rawStart),
      endCalendarDate: toPickerCalendarDate(rawEnd),
    },
    today,
  );
}

/** Reads a picker day as `yyyy-MM-dd`; a `Date` value carries the picked day in local time. */
function toPickerCalendarDate(value: string | Date): string {
  return typeof value === "string" ? value : format(value, PRACTICE_CALENDAR_DATE_FORMAT);
}

/**
 * Renders the eligibility Show control: gray Show label, calendar trigger,
 * preset menu, and Cancel/Apply calendar panel.
 * Works in practice-local calendar dates; the caller turns them into instants.
 */
export default function EligibilityDateRangeControl({
  calendarDateRange,
  onCalendarDateRangeChange,
  today,
  isDisabled = false,
}: EligibilityDateRangeControlProps) {
  const { startCalendarDate, endCalendarDate } = calendarDateRange;
  const preset: EligibilityDateRangePreset = isDefaultEligibilityAppointmentCalendarDateRange(
    calendarDateRange,
    today,
  )
    ? ELIGIBILITY_DATE_RANGE_PRESET_NEXT_TWO_WEEKS
    : ELIGIBILITY_DATE_RANGE_PRESET_CUSTOM;
  const [menuOpened, setMenuOpened] = useState(false);
  const [menuView, setMenuView] = useState<EligibilityDateRangeMenuView>("menu");
  const [draftCustomRange, setDraftCustomRange] = useState<DatesRangeValue>(() =>
    toDatePickerRangeValue(calendarDateRange),
  );
  const menuCloseReasonRef = useRef<"commit" | "dismiss" | null>(null);

  useEffect(() => {
    setDraftCustomRange([startCalendarDate, endCalendarDate]);
  }, [endCalendarDate, startCalendarDate]);

  const triggerLabel =
    preset === ELIGIBILITY_DATE_RANGE_PRESET_CUSTOM
      ? formatEligibilityAppointmentDateRangeLabel(calendarDateRange)
      : ELIGIBILITY_DATE_RANGE_PRESET_NEXT_TWO_WEEKS_LABEL;

  const draftMaxDate = useMemo(() => {
    const [rawStart] = draftCustomRange;
    if (!rawStart) return undefined;
    return getEligibilityAppointmentDateRangeMaxEndDate(toPickerCalendarDate(rawStart));
  }, [draftCustomRange]);

  const canApplyCustomRange = Boolean(draftCustomRange[0] && draftCustomRange[1]);

  const resetDraftAndMenuView = () => {
    setDraftCustomRange(toDatePickerRangeValue(calendarDateRange));
    setMenuView("menu");
  };

  const handleMenuOpenChange = (opened: boolean) => {
    if (opened) {
      setMenuOpened(true);
      return;
    }
    const closeReason = menuCloseReasonRef.current;
    menuCloseReasonRef.current = null;
    setMenuOpened(false);
    if (closeReason === "commit") {
      setMenuView("menu");
      return;
    }
    resetDraftAndMenuView();
  };

  const handleSelectNextTwoWeeks = () => {
    const nextTwoWeeksRange = getDefaultEligibilityAppointmentCalendarDateRange(today);
    menuCloseReasonRef.current = "commit";
    setDraftCustomRange(toDatePickerRangeValue(nextTwoWeeksRange));
    setMenuView("menu");
    setMenuOpened(false);
    onCalendarDateRangeChange(nextTwoWeeksRange);
  };

  const handleSelectCustomRange = () => {
    setDraftCustomRange(toDatePickerRangeValue(calendarDateRange));
    setMenuView("calendar");
  };

  const handleCancelCustomRange = () => {
    menuCloseReasonRef.current = "dismiss";
    resetDraftAndMenuView();
    setMenuOpened(false);
  };

  const handleApplyCustomRange = () => {
    const resolvedRange = resolveCalendarDateRangeFromPickerValue(draftCustomRange, today);
    if (!resolvedRange) return;
    menuCloseReasonRef.current = "commit";
    setDraftCustomRange(toDatePickerRangeValue(resolvedRange));
    setMenuView("menu");
    setMenuOpened(false);
    onCalendarDateRangeChange(resolvedRange);
  };

  return (
    <Group gap="xs" wrap="wrap">
      <TextWrapper size="sm" lh="20px" c="gray.7" variant="bold">
        Show:
      </TextWrapper>
      <Menu
        shadow="md"
        radius="md"
        withinPortal
        opened={menuOpened}
        position="bottom-end"
        closeOnItemClick={false}
        onChange={handleMenuOpenChange}
        transitionProps={{ duration: 0 }}
        width={menuView === "menu" ? 280 : "auto"}
      >
        <Menu.Target>
          <Button
            radius="sm"
            variant="default"
            disabled={isDisabled}
            data-testid="eligibility-date-range-preset"
            aria-label="Show appointments for date range"
            rightSection={<IconChevronDown size={20} stroke={1.5} />}
            leftSection={
              <IconCalendarMinus size={20} stroke={1.5} color="var(--mantine-color-gray-6)" />
            }
            styles={{
              root: {
                borderColor: "var(--mantine-color-cream-4)",
                backgroundColor: "var(--mantine-color-white)",
                color: "var(--mantine-color-burgundy-9)",
                fontWeight: 600,
                fontSize: 14,
                paddingInline: 14,
                paddingBlock: 8,
                height: "auto",
              },
              section: {
                marginInline: 0,
              },
              inner: {
                gap: 10,
              },
            }}
          >
            {triggerLabel}
          </Button>
        </Menu.Target>
        <Menu.Dropdown
          data-testid={
            menuView === "calendar"
              ? "eligibility-date-range-custom"
              : "eligibility-date-range-menu"
          }
        >
          {menuView === "menu" ? (
            <>
              <Menu.Item
                py="md"
                px="lg"
                fw={600}
                onClick={handleSelectNextTwoWeeks}
                data-testid="eligibility-date-range-next-two-weeks-option"
              >
                {ELIGIBILITY_DATE_RANGE_PRESET_NEXT_TWO_WEEKS_LABEL}
              </Menu.Item>
              <Menu.Item
                py="md"
                px="lg"
                fw={600}
                onClick={handleSelectCustomRange}
                data-testid="eligibility-date-range-custom-option"
              >
                {ELIGIBILITY_DATE_RANGE_PRESET_CUSTOM_LABEL}
              </Menu.Item>
            </>
          ) : (
            <Box p="lg">
              <Stack gap="lg">
                <DatePicker
                  type="range"
                  minDate={today}
                  allowSingleDateInRange
                  maxDate={draftMaxDate}
                  value={draftCustomRange}
                  onChange={setDraftCustomRange}
                  data-testid="eligibility-date-range-picker"
                />
                <Group gap="lg" wrap="nowrap">
                  <Button
                    color="burgundy"
                    variant="outline"
                    onClick={handleCancelCustomRange}
                    data-testid="eligibility-date-range-cancel"
                  >
                    Cancel
                  </Button>
                  <Button
                    w={179}
                    color="burgundy"
                    disabled={!canApplyCustomRange}
                    onClick={handleApplyCustomRange}
                    data-testid="eligibility-date-range-apply"
                  >
                    Apply
                  </Button>
                </Group>
              </Stack>
            </Box>
          )}
        </Menu.Dropdown>
      </Menu>
    </Group>
  );
}
